<template>
  <div class="owner-invoice-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('invoiceHeader')" :subtitle="$t('ownerInvoice.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.ownerName" clearable class="w-full"
            :placeholder="$t('ownerNamePlaceholder')" @keyup.enter.native="fetchData" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.invoiceType" clearable class="w-full"
            :placeholder="$t('invoiceTypePlaceholder')">
            <el-option value="" :label="$t('invoiceTypePlaceholder')" />
            <el-option value="1001" :label="$t('personal')" />
            <el-option value="2002" :label="$t('enterprise')" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.invoiceName" clearable class="w-full"
            :placeholder="$t('invoiceHeaderPlaceholder')" @keyup.enter.native="fetchData" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="fetchData">
        <template #left>
          <span class="ds-panel__title">{{ $t('invoiceHeader') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('ownerInvoice.page.empty')" @retry="fetchData">
        <el-table-column prop="oiId" :label="$t('serialNumber')" align="center" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.oiId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('ownerName')" align="left" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.ownerName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="invoiceType" :label="$t('invoiceType')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.invoiceType === '1001' ? 'info' : 'primary'"
              :label="scope.row.invoiceType === '1001' ? $t('personal') : $t('enterprise')" />
          </template>
        </el-table-column>
        <el-table-column prop="invoiceName" :label="$t('invoiceHeader')" align="left" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.invoiceName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="invoiceNum" :label="$t('taxpayerId')" align="center" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.invoiceNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="invoiceAddress" :label="$t('address')" align="left" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.invoiceAddress || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="invoiceLink" :label="$t('phone')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.invoiceLink || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="invoiceAccount" :label="$t('bankAccount')" align="left" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.invoiceAccount || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('remark')" align="left" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('operation')" align="center" fixed="right" width="150">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('modify') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('ownerInvoice.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 编辑组件 -->
    <edit-owner-invoice ref="editDialog" @success="fetchData" />

    <!-- 删除组件 -->
    <delete-owner-invoice ref="deleteDialog" @success="fetchData" />
  </div>
</template>

<script>
import { listOwnerInvoice } from '@/api/fee/ownerInvoiceApi'
import EditOwnerInvoice from '@/components/fee/editOwnerInvoice'
import DeleteOwnerInvoice from '@/components/fee/deleteOwnerInvoice'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'OwnerInvoiceList',
  components: {
    EditOwnerInvoice,
    DeleteOwnerInvoice,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        ownerName: '',
        invoiceType: '',
        invoiceName: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }

        const response = await listOwnerInvoice(params)
        this.tableData = Array.isArray(response.data) ? response.data : []
        this.pagination.total = response.total || 0
      } catch (error) {
        console.error('获取发票抬头失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('ownerInvoice.page.loadFailed')
        this.$message.error(error.message || this.$t('common.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchData()
    },
    handleReset() {
      this.searchForm.ownerName = ''
      this.searchForm.invoiceType = ''
      this.searchForm.invoiceName = ''
      this.handleSearch()
    },
    handleAdd() {
      this.$router.push('/views/fee/addOwnerInvoice')
    },
    handleEdit(row) {
      this.$refs.editDialog.open({ ...row })
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handlePageChange(page) {
      this.pagination.current = page
      this.fetchData()
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.fetchData()
    }
  }
}
</script>

<style lang="scss" scoped>
.owner-invoice-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
